// Starting up Alpine
import Alpine from 'alpinejs'
import focus from '@alpinejs/focus'
import ui from '@alpinejs/ui'
import intersect from '@alpinejs/intersect'
import { CountUp } from 'countup.js'

window.Alpine = Alpine
Alpine.plugin(focus)
Alpine.plugin(ui)
Alpine.plugin(intersect)

Alpine.data('statCounter', (raw) => ({
	raw: String(raw ?? ''),
	started: false,
	counter: null,
	init() {
		const match = this.raw.match(/^([^\d]*)([\d,]+(?:\.\d+)?)(.*)$/)
		if (!match) return
		const [, prefix, numStr, suffix] = match
		const value = parseFloat(numStr.replace(/,/g, ''))
		const decimals = (numStr.split('.')[1] || '').length
		this.counter = new CountUp(this.$el, value, {
			startVal: 0,
			duration: 1.6,
			prefix,
			suffix,
			decimalPlaces: decimals,
			separator: ',',
			useEasing: true,
			easingFn: (t, b, c, d) => {
				const x = t / d - 1
				return -c * (x * x * x * x - 1) + b
			},
		})
		if (!this.counter.error) {
			const zero = (0).toFixed(decimals)
			this.$el.textContent = `${prefix}${zero}${suffix}`
		}
	},
	start() {
		if (this.started || !this.counter || this.counter.error) return
		this.started = true
		this.counter.start()
	},
}))

Alpine.data('scrollInteraction', () => ({
	activeSectionId: null,
	layerAUrl: '',
	layerBUrl: '',
	activeLayer: 'A',

	init() {
		// Multiple feature-scroll blocks can co-exist, so scope to this root.
		const firstSection = this.$el.querySelector('[data-image-src]')
		if (firstSection && firstSection.dataset.imageSrc) {
			this.activeSectionId = firstSection.id || null
			this.layerAUrl = firstSection.dataset.imageSrc
			this.activeLayer = 'A'
		}
	},

	setActiveSection(sectionId, element) {
		const newImageUrl = element.dataset.imageSrc
		const currentUrl = this.activeLayer === 'A' ? this.layerAUrl : this.layerBUrl
		if (!newImageUrl || newImageUrl === currentUrl) return

		this.activeSectionId = sectionId

		// Preload the incoming image so the crossfade only starts once it can
		// paint — the outgoing layer stays visible underneath until then.
		const incoming = this.activeLayer === 'A' ? 'B' : 'A'
		const swap = () => {
			// Ignore stale loads if the section changed again mid-flight.
			if (this.activeSectionId !== sectionId) return
			if (incoming === 'A') this.layerAUrl = newImageUrl
			else this.layerBUrl = newImageUrl
			this.activeLayer = incoming
		}

		const preloader = new Image()
		preloader.onload = swap
		preloader.onerror = swap
		preloader.src = newImageUrl
	},
}))

Alpine.data('videoPlaylist', () => ({
	canScrollLeft: false,
	canScrollRight: true,
	modalOpen: false,
	modalIndex: -1,
	videoSources: [],
	resizeHandler: null,

	init() {
		this.resizeHandler = () => this.updateArrows()
		window.addEventListener('resize', this.resizeHandler)

		this.$nextTick(() => {
			this.$refs.scrollContainer.scrollLeft = 0
			this.updateArrows()
			requestAnimationFrame(() => this.updateArrows())
			this.videoSources = [
				...this.$refs.scrollContainer.querySelectorAll('video'),
			].map((v) => v.src)
		})
	},

	get hasPrev() {
		return this.modalIndex > 0
	},
	get hasNext() {
		return this.modalIndex < this.videoSources.length - 1
	},

	updateArrows() {
		const el = this.$refs.scrollContainer
		if (!el) return
		this.canScrollLeft = el.scrollLeft > 10
		this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 10
	},

	scrollLeft() {
		const el = this.$refs.scrollContainer
		el.scrollBy({ left: -this.getScrollStep(), behavior: 'smooth' })
	},

	scrollRight() {
		const el = this.$refs.scrollContainer
		el.scrollBy({ left: this.getScrollStep(), behavior: 'smooth' })
	},

	getScrollStep() {
		const el = this.$refs.scrollContainer
		if (!el) return 0

		const card = el.querySelector('.video-playlist-card')
		if (!card) return el.clientWidth * 0.8

		const styles = window.getComputedStyle(el)
		const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0
		const cardsPerStep = el.clientWidth < 768 ? 2 : 3
		return (card.getBoundingClientRect().width + gap) * cardsPerStep
	},

	playPreview(cardEl) {
		if (this.modalOpen) return
		const video = cardEl.querySelector('video')
		if (!video) return
		video.play().catch(() => {})
		video._previewTimer = setTimeout(() => {
			video.pause()
			video.currentTime = 0
		}, 10000)
	},

	stopPreview(cardEl) {
		const video = cardEl.querySelector('video')
		if (!video) return
		clearTimeout(video._previewTimer)
		video.pause()
		video.currentTime = 0
	},

	openModal(cardEl) {
		const video = cardEl.querySelector('video')
		if (!video) return
		clearTimeout(video._previewTimer)
		video.pause()
		video.currentTime = 0
		this.modalIndex = this.videoSources.indexOf(video.src)
		this.loadModalVideo(video.src)
	},

	loadModalVideo(src) {
		const modal = this.$refs.modalVideo
		modal.pause()
		modal.src = src
		this.modalOpen = true
		document.body.style.overflow = 'hidden'
		this.$nextTick(() => modal.play().catch(() => {}))
	},

	modalPrev() {
		if (!this.modalOpen || !this.hasPrev) return
		this.modalIndex--
		this.loadModalVideo(this.videoSources[this.modalIndex])
	},

	modalNext() {
		if (!this.modalOpen || !this.hasNext) return
		this.modalIndex++
		this.loadModalVideo(this.videoSources[this.modalIndex])
	},

	async shareVideo() {
		const url = this.videoSources[this.modalIndex]
		if (!url) return
		if (navigator.share) {
			navigator.share({ url }).catch(() => {})
		} else {
			await navigator.clipboard.writeText(url)
			const label = this.$refs.shareLabel
			label.textContent = 'Copied!'
			setTimeout(() => {
				label.textContent = 'Share'
			}, 2000)
		}
	},

	closeModal() {
		this.modalOpen = false
		this.modalIndex = -1
		const modal = this.$refs.modalVideo
		modal.pause()
		modal.removeAttribute('src')
		document.body.style.overflow = ''
	},

	destroy() {
		if (this.resizeHandler) {
			window.removeEventListener('resize', this.resizeHandler)
		}
	},
}))

Alpine.data('pageNav', (initial, opts = {}) => ({
	atElement: initial,
	open: false,
	labels: opts.labels || [],
	activeLabel: opts.activeLabel || '',
	spy: !!opts.spy,
	canLeft: false,
	canRight: false,
	overflowing: false,
	isDesktop: false,
	init() {
		this.desktopQuery = window.matchMedia('(min-width: 768px)')
		this.isDesktop = this.desktopQuery.matches
		this.onDesktopChange = (e) => {
			this.isDesktop = e.matches
			if (e.matches) this.open = false
			this.measure()
		}
		this.desktopQuery.addEventListener('change', this.onDesktopChange)

		this.onResize = () => this.measure()
		window.addEventListener('resize', this.onResize)
		window.addEventListener('load', this.onResize)

		this.$nextTick(() => this.measure())
		document.fonts?.ready.then(() => this.measure())
	},
	destroy() {
		this.desktopQuery?.removeEventListener('change', this.onDesktopChange)
		window.removeEventListener('resize', this.onResize)
		window.removeEventListener('load', this.onResize)
	},
	get currentLabel() {
		if (this.spy) {
			const match = this.labels.find((l) => l.id === this.atElement)
			if (match) return match.label
		}
		return this.activeLabel
	},
	toggle() {
		this.open = !this.open
	},
	close() {
		this.open = false
	},
	measure() {
		const el = this.$refs.scroller
		if (!el) return
		const max = el.scrollWidth - el.clientWidth
		this.overflowing = max > 1
		this.canLeft = el.scrollLeft > 1
		this.canRight = el.scrollLeft < max - 1
	},
	nudge(direction) {
		const el = this.$refs.scroller
		if (!el) return
		const reduce = window.matchMedia(
			'(prefers-reduced-motion: reduce)',
		).matches
		el.scrollBy({
			left: Math.round(el.clientWidth * 0.6) * direction,
			behavior: reduce ? 'auto' : 'smooth',
		})
	},
}))

Alpine.start()
